import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { dashApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldAlert, ArrowLeft, KeyRound, Loader2, Lock, UserCircle } from "lucide-react";
import { AuthDisclaimer } from "@/components/AuthDisclaimer";

const Account = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({ old_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.old_password || !form.new_password) return toast.error("Tous les champs sont requis");
    if (form.new_password.length < 6) return toast.error("Le nouveau mot de passe doit faire 6 caractères minimum");
    if (form.new_password !== form.confirm) return toast.error("La confirmation ne correspond pas");
    setSaving(true);
    try {
      await dashApi.changePassword({ old_password: form.old_password, new_password: form.new_password });
      toast.success("Mot de passe modifié avec succès");
      setForm({ old_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la modification");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100">
      <header className="h-16 sticky top-0 z-40 backdrop-blur-md bg-slate-900/80 border-b border-slate-800 px-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-950 border border-emerald-500/30 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h1 className="font-display font-bold leading-tight flex items-center gap-2"><UserCircle className="w-4 h-4 text-emerald-400" /> Mon compte</h1>
            <p className="text-[11px] font-mono text-slate-500">Sama Jaamu — Paramètres de sécurité</p>
          </div>
        </div>
        <Button data-testid="account-back-btn" onClick={() => navigate("/")} variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour au dashboard
        </Button>
      </header>

      <main className="max-w-xl mx-auto px-4 py-10">
        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-800">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center shrink-0"><UserCircle className="w-6 h-6 text-emerald-400" /></div>
            <div className="min-w-0">
              <p className="font-display font-semibold text-slate-100 truncate">{user?.name}</p>
              <p className="text-xs font-mono text-slate-400 truncate">{user?.email} · <span className="uppercase text-emerald-400">{user?.role}</span></p>
            </div>
          </div>

          <h2 className="font-display font-semibold text-lg flex items-center gap-2 mb-5"><KeyRound className="w-5 h-5 text-yellow-400" /> Changer mon mot de passe</h2>
          <form onSubmit={submit} className="space-y-4">
            <Field label="Mot de passe actuel" testid="account-old-password" value={form.old_password} onChange={set("old_password")} />
            <Field label="Nouveau mot de passe" testid="account-new-password" value={form.new_password} onChange={set("new_password")} />
            <Field label="Confirmer le nouveau mot de passe" testid="account-confirm-password" value={form.confirm} onChange={set("confirm")} />
            <Button type="submit" data-testid="account-save-btn" disabled={saving} className="w-full h-11 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer le nouveau mot de passe"}
            </Button>
          </form>
          <AuthDisclaimer />
        </div>
      </main>
    </div>
  );
};

const Field = ({ label, testid, value, onChange }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-mono uppercase tracking-wider text-slate-400">{label}</Label>
    <div className="relative">
      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
      <Input data-testid={testid} type="password" value={value} onChange={onChange} className="pl-10 bg-slate-950 border-slate-700 text-slate-100 h-11" placeholder="••••••••••" />
    </div>
  </div>
);

export default Account;
